// =============================================
// Kind of Document — add / edit form (acctg_docu_type, migration 142)
// =============================================
// Opened from the Kind of Document picker in Process Docs (Filing + Bookkeeping):
// "+ Add new kind of document" at the top of the list, "Add “…”" for what was typed,
// or the pencil on a kind's row.
//
// A kind decides which side a document is on (vendor / customer) and which tax reports
// count it — that comes from the STANDARD kind. So an account's own kind always says
// which standard kind it works like, and a standard kind can only be renamed (or taken
// off this account's list); everyone else keeps the standard one. The parent saves.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiX } from 'react-icons/fi';
import SearchableDropdown, { DropdownOption } from '../../../components/ui/SearchableDropdown';
import { setFormDirty, isFormDirty, registerSaveHandler, confirmDiscardChanges } from '../../../lib/unsavedChanges';
import { useDragOffset } from '../../../lib/useDragOffset';

export interface KindFormValues {
  name: string;
  worksLikeId: string;      // the standard kind an own kind works like
}

export interface KindFormProps {
  /** null = a new kind */
  editing: { kind: 'own' | 'standard' | 'edited-standard'; values: KindFormValues; standardName: string } | null;
  initial?: Partial<KindFormValues>;
  /** the standard kinds (for "Works like") */
  standardOptions: DropdownOption[];
  /** lower-case names already in the list (this kind's own name left out) */
  takenNames: Set<string>;
  onSave: (v: KindFormValues) => Promise<boolean>;
  onRemove?: () => Promise<boolean>;
  onBackToStandard?: () => Promise<boolean>;
  onClose: () => void;
}

const KindFormModal: React.FC<KindFormProps> = ({
  editing, initial, standardOptions, takenNames, onSave, onRemove, onBackToStandard, onClose,
}) => {
  const start = useMemo<KindFormValues>(() => ({ name: '', worksLikeId: '', ...(editing ? editing.values : initial) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
  const [v, setV] = useState<KindFormValues>(start);
  const [errors, setErrors] = useState<{ name?: string; base?: string }>({});
  const [busy, setBusy] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const drag = useDragOffset();
  const kind = editing?.kind;
  const isStandard = kind === 'standard' || kind === 'edited-standard';

  const set = (patch: Partial<KindFormValues>) => { setV(prev => ({ ...prev, ...patch })); setFormDirty(true); };

  const validate = (): boolean => {
    const e: { name?: string; base?: string } = {};
    const name = v.name.trim();
    if (!name) e.name = 'Give the kind a name, e.g. Gas slip';
    else if (takenNames.has(name.toLowerCase())) e.name = `“${name}” is already in the list`;
    if (!isStandard && !v.worksLikeId) e.base = 'Choose which standard kind it works like';
    setErrors(e);
    return !e.name && !e.base;
  };

  const save = async (): Promise<boolean> => {
    if (busy || !validate()) return false;
    setBusy(true);
    try {
      const ok = await onSave({ ...v, name: v.name.trim().replace(/\s+/g, ' ') });
      if (ok) setFormDirty(false);
      return ok;
    } finally { setBusy(false); }
  };
  const saveRef = useRef(save);
  saveRef.current = save;

  useEffect(() => {
    registerSaveHandler(() => saveRef.current());
    return () => registerSaveHandler(null);
  }, []);
  useEffect(() => { setTimeout(() => nameRef.current?.focus(), 0); }, []);

  const dismiss = async () => {
    if (isFormDirty() && !(await confirmDiscardChanges())) return;
    onClose();
  };
  const run = async (fn?: () => Promise<boolean>) => {
    if (!fn || busy) return;
    setBusy(true);
    try { if (await fn()) setFormDirty(false); } finally { setBusy(false); }
  };

  const title = !editing ? 'Add new kind of document'
    : isStandard ? `Edit standard kind “${editing.standardName}”` : `Edit “${start.name}”`;

  return (
    <div className="pr-mf-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) dismiss(); }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !(e.target as HTMLElement).closest('.sd-wrapper')) { e.stopPropagation(); dismiss(); }
        if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'Enter')) { e.preventDefault(); e.stopPropagation(); save().then(ok => ok && onClose()); }
      }}>
      <div className="pr-mf-modal pr-nature-modal" role="dialog" aria-modal="true" aria-label={title} style={drag.style}>
        <div className="pr-mf-modal-head pr-modal-drag" onMouseDown={drag.onHeaderMouseDown} title="Drag to move">
          <span className="pr-mf-modal-title">{title}</span>
          <button className="pr-mf-modal-close" onClick={dismiss} title="Close" type="button"><FiX size={18} /></button>
        </div>
        <form className="pr-mf-modal-body pr-form-outlined pr-nature-form"
          onSubmit={(e) => { e.preventDefault(); save().then(ok => ok && onClose()); }}>
          {isStandard && (
            <p className="pr-nature-note">
              This is a standard kind. You can rename it for your account (everyone else keeps
              “{editing!.standardName}”) or take it off your list. How it is booked stays the same.
            </p>
          )}

          <div className="pr-form-group">
            <label className="pr-form-label" htmlFor="pr-kind-name">Kind of document <span className="required">*</span></label>
            <input id="pr-kind-name" ref={nameRef} className="pr-form-input" type="text" maxLength={100}
              value={v.name} placeholder="e.g. Gas slip, Cheque copy, Rent receipt"
              onChange={e => set({ name: e.target.value })} />
            {v.name && (
              <button type="button" className="pr-nature-clear" title="Clear" aria-label="Clear the name"
                onClick={() => { set({ name: '' }); nameRef.current?.focus(); }}><FiX size={14} /></button>
            )}
            {errors.name && <span className="pr-form-error">{errors.name}</span>}
          </div>

          {!isStandard && (
            <div className="pr-form-group">
              <label className="pr-form-label">Works like <span className="required">*</span></label>
              <SearchableDropdown className="pr-form-select" options={standardOptions} value={v.worksLikeId}
                onChange={val => set({ worksLikeId: val })} placeholder="Expense Receipt, Bank - Deposit…" hasError={!!errors.base} />
              {errors.base
                ? <span className="pr-form-error">{errors.base}</span>
                : <span className="pr-form-hint">Decides vendor or customer, and which tax reports count it.</span>}
            </div>
          )}

          <div className="pr-nature-actions">
            {onBackToStandard && (
              <button type="button" className="pr-reject-btn" disabled={busy} onClick={() => run(onBackToStandard)}>Back to standard</button>
            )}
            {onRemove && (
              <button type="button" className="pr-reject-btn" disabled={busy} onClick={() => run(onRemove)}>
                {kind === 'own' ? 'Delete' : 'Remove from my list'}
              </button>
            )}
            <span className="pr-nature-actions-gap" />
            <button type="button" className="pr-nature-cancel" onClick={dismiss}>Cancel</button>
            <button type="submit" className="pr-submit-btn" disabled={busy} title="Save (Ctrl+S)">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default KindFormModal;
